/**
 * API client: Bearer token injection, 401 auth reset, and 503 retry.
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';
import { API_URL } from './apiUrl';
import { exchangePlatformToken } from './platformToken';

const apiUrl = API_URL;

const apiClient: AxiosInstance = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
});

// В embed'е личность даёт платформа (CU-869e4mvzp) — её access приоритетнее
// локального: standalone-токен мог остаться в localStorage от прошлой сессии
// в этом же браузере и увёл бы запросы под чужого пользователя.
apiClient.interceptors.request.use((config) => {
  const { platformAccessToken, accessToken } = useAuthStore.getState();
  const bearer = platformAccessToken || accessToken;
  if (bearer) config.headers['Authorization'] = `Bearer ${bearer}`;
  return config;
});

// 401-каналы, которые НЕ запускают refresh-ретрай: их 401 — штатный ответ
// (неверный пароль / невалидный токен), повторять и сбрасывать auth не надо.
const AUTH_EXEMPT_URLS = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/admin/login',
  '/api/admin/check',
  '/api/platform/token',
];

interface RetryableAuthConfig extends InternalAxiosRequestConfig {
  _authRetried?: boolean;
}

// Дедупликация: параллельные 401 не должны порождать N запросов на refresh.
let refreshPromise: Promise<boolean> | null = null;
let platformExchangePromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) return false;
  try {
    // Голый axios (без интерсепторов apiClient), чтобы refresh не зациклился.
    const { data } = await axios.post(
      `${apiUrl}/api/auth/refresh`,
      { refresh_token: refreshToken },
      { withCredentials: true },
    );
    useAuthStore.getState().applyTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    useAuthStore.getState().resetAuth();
    return false;
  }
}

// Платформенный access живёт ~30 минут. Полагаться на таймер нельзя: браузер
// тормозит таймеры в фоновой вкладке, а сон машины их вовсе пропускает — поэтому
// источник истины именно 401: меняем refresh на новый access и повторяем запрос
// ОДИН раз. Второй 401 (config._authRetried) — настоящий отказ, пробрасываем.
async function reexchangePlatformToken(): Promise<boolean> {
  const { platformRefreshToken } = useAuthStore.getState();
  if (!platformRefreshToken) return false;
  try {
    const accessToken = await exchangePlatformToken(platformRefreshToken);
    useAuthStore.getState().setPlatformAccessToken(accessToken);
    return true;
  } catch {
    // Refresh мёртв (401 refresh_token_invalid) или платформа недоступна (502):
    // молча продлить сессию нечем — гасим платформенный канал, 401 уходит наверх.
    useAuthStore.getState().clearPlatformAuth();
    return false;
  }
}

// 401 → обновляем access и повторяем запрос ровно один раз.
// - embed (CU-869e4mvzp): переобмен refresh-токена aseller.io на новый access;
// - standalone (CU-869dt3dz6): refresh собственного HS256-токена.
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryableAuthConfig | undefined;
    const url: string = config?.url ?? '';

    if (
      error.response?.status !== 401 ||
      !config ||
      config._authRetried ||
      AUTH_EXEMPT_URLS.some((exempt) => url.startsWith(exempt))
    ) {
      return Promise.reject(error);
    }

    const { platformRefreshToken, refreshToken } = useAuthStore.getState();

    if (platformRefreshToken) {
      config._authRetried = true;
      if (!platformExchangePromise) {
        platformExchangePromise = reexchangePlatformToken().finally(() => {
          platformExchangePromise = null;
        });
      }
      if (await platformExchangePromise) {
        config.headers['Authorization'] =
          `Bearer ${useAuthStore.getState().platformAccessToken}`;
        return apiClient.request(config);
      }
      return Promise.reject(error);
    }

    if (refreshToken) {
      config._authRetried = true;
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      if (await refreshPromise) {
        config.headers['Authorization'] = `Bearer ${useAuthStore.getState().accessToken}`;
        return apiClient.request(config);
      }
    }

    return Promise.reject(error);
  },
);

/**
 * CU-869dnpp43: при 503 (БД временно недоступна, save verification failed)
 * автоматически повторяем запрос до 2 раз, уважая Retry-After header.
 * Без этого пользователь видел бы провал и должен был сам нажимать кнопку.
 */
const MAX_RETRIES_ON_503 = 2;
const MAX_RETRY_AFTER_SECONDS = 30;

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryableConfig | undefined;
    if (!config || error.response?.status !== 503) return Promise.reject(error);

    config._retryCount = (config._retryCount ?? 0) + 1;
    if (config._retryCount > MAX_RETRIES_ON_503) return Promise.reject(error);

    const retryAfterHeader = error.response.headers['retry-after'];
    const retryAfterSec = Math.min(
      MAX_RETRY_AFTER_SECONDS,
      Math.max(1, parseInt(String(retryAfterHeader ?? '1'), 10) || 1),
    );

    await new Promise<void>((resolve) => setTimeout(resolve, retryAfterSec * 1000));
    return apiClient.request(config);
  },
);

export default apiClient;

/* ── Keepa types ── */

export interface PackageDimensions {
  length: number | null;
  width: number | null;
  height: number | null;
}

export interface KeepaProductData {
  asin: string;
  buy_box_price: number | null;
  price_source: string | null;
  fba_fee: number | null;
  referral_fee: number | null;
  monthly_sales: number | null;
  package_weight_kg: number | null;
  volumetric_weight_kg: number | null;
  package_dimensions_cm: PackageDimensions | null;
  bsr_data: number[] | null;
  price_history: number[] | null;
  error: string | null;
}

export interface KeepaHistoryPoint {
  x: number;
  y: number;
}

export interface KeepaHistoryResponse {
  asin: string;
  title: string | null;
  price: KeepaHistoryPoint[];
  bsr: KeepaHistoryPoint[];
  window_days: number;
  price_points: number;
  bsr_points: number;
}

export interface MonthlyForecast {
  month: number;
  month_name: string;
  sales: number;
  revenue: number;
  profit: number;
  cumulative_profit: number;
}

export interface ProfitForecastResponse {
  profit_per_unit: number;
  batch_quantity: number;
  batch_profit: number;
  forecast: MonthlyForecast[];
  keepa_data: KeepaProductData | null;
  seasonality: number[] | null;
}

/* ── API functions ── */

export async function fetchKeepaData(
  asins: string[],
  signal?: AbortSignal,
): Promise<Record<string, KeepaProductData>> {
  const { data } = await apiClient.post('/api/keepa-data', { asins }, { signal });
  return data;
}

export async function fetchKeepaHistory(
  asin: string,
  windowDays = 365,
): Promise<KeepaHistoryResponse> {
  const { data } = await apiClient.get('/api/keepa-history', {
    params: { asin, window_days: windowDays },
  });
  return data;
}

export interface CompetitorData {
  asin: string;
  title?: string;
  image?: string;
  price?: number;
  brand?: string;
  rating?: string;
  reviews?: string;
  bsr?: string;
  clickShare?: number;
  monthlySales?: number;
}

export interface NicheData {
  nicheId: string;
  nicheTitle: string;
  products: CompetitorData[];
  count: number;
}

export interface CompetitorsResponse {
  niches: NicheData[];
  competitors: CompetitorData[];
  nicheTitle?: string;
  totalFound?: number;
}

export async function fetchCompetitors(
  asin: string,
): Promise<CompetitorsResponse> {
  const { data } = await apiClient.post('/proxy/competitors', { asin });
  return {
    niches: data.niches ?? [],
    competitors: data.competitors ?? [],
    nicheTitle: data.nicheTitle,
    totalFound: data.totalFound,
  };
}

export async function fetchProfitForecast(params: {
  asin: string;
  competitor_asins?: string[];
  storage_fee_per_unit: number;
  purchase_price: number;
}): Promise<ProfitForecastResponse> {
  const { data } = await apiClient.post(
    '/api/calculate-profit-forecast',
    params,
  );
  return data;
}

/* ── Save / Share / Export ── */

export async function saveResult(
  fileName: string,
  calcData: Record<string, unknown>,
): Promise<{ success: boolean; message: string }> {
  const { data } = await apiClient.post('/api/save-result', {
    fileName,
    data: calcData,
  });
  return data;
}

export async function createShareLink(
  fileName: string,
): Promise<{ share_id: string; url: string }> {
  const { data } = await apiClient.post('/api/share', { fileName });
  return data;
}

export async function fetchSharedResult(
  shareId: string,
): Promise<Record<string, unknown>> {
  const { data } = await apiClient.get(`/api/share/${shareId}`);
  return data;
}

export async function exportXlsx(
  payload: Record<string, unknown>,
): Promise<Blob> {
  const { data } = await apiClient.post('/api/export-xlsx', payload, {
    responseType: 'blob',
  });
  return data;
}

/* ── History / User Results ── */

export interface HistoryVersion {
  fileName: string;
  parent_asin: string;
  timestamp: string;
  is_variation: boolean;
  variation_idea: string;
  selling_price: number | null;
  monthly_sales: number | null;
  owner_id?: string;
}

export interface HistoryGroup {
  asin: string;
  latest_timestamp: string;
  versions_count: number;
  versions: HistoryVersion[];
}

export async function fetchUserResults(): Promise<{ groups: HistoryGroup[] }> {
  const { data } = await apiClient.get('/api/user-results');
  return data;
}

export async function claimShare(shareId: string): Promise<{ success: boolean }> {
  const { data } = await apiClient.post('/api/claim-share', { share_id: shareId });
  return data;
}

export async function loadSavedResult(filename: string): Promise<Record<string, unknown>> {
  const { data } = await apiClient.get(`/api/saved-results/${filename}`);
  return data;
}

/* ── Admin: user management (CU local-auth) ── */

export interface AdminUser {
  id: number;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string | null;
}

export async function listAdminUsers(): Promise<AdminUser[]> {
  const { data } = await apiClient.get('/api/auth/users');
  return data.users;
}

export async function createAdminUser(payload: {
  email: string;
  password: string;
  is_admin: boolean;
}): Promise<AdminUser> {
  const { data } = await apiClient.post('/api/auth/users', payload);
  return data;
}

export async function setAdminUserRole(
  userId: number,
  isAdmin: boolean,
): Promise<AdminUser> {
  const { data } = await apiClient.patch(`/api/auth/users/${userId}/role`, {
    is_admin: isAdmin,
  });
  return data;
}

export async function deactivateAdminUser(userId: number): Promise<void> {
  await apiClient.delete(`/api/auth/users/${userId}`);
}

export async function changePassword(payload: {
  current_password: string;
  new_password: string;
}): Promise<{ message: string }> {
  const { data } = await apiClient.post('/api/auth/change-password', payload);
  return data;
}
